import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import { apiGet, apiPost } from '../../services/api';

export default function AdminSecurityTab({ token, handleAuthError }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [hasSecret, setHasSecret] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Disable flow
  const [disabling, setDisabling] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  // QR flow
  const [qrStep, setQrStep] = useState('idle'); // 'idle' | 'loading' | 'qr'
  const [qrUrl, setQrUrl] = useState('');
  const [qrSecret, setQrSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [qrError, setQrError] = useState('');
  const [qrSubmitting, setQrSubmitting] = useState(false);
  const [qrMode, setQrMode] = useState(''); // 'enable' | 'reset'

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/v1/auth/2fa/status', token);
      if (handleAuthError(res)) return;
      const data = await res.json().catch(() => ({}));
      setEnabled(Boolean(data?.data?.enabled));
      setHasSecret(Boolean(data?.data?.has_secret));
      setConfirmedAt(data?.data?.confirmed_at ?? null);
    } catch {
      setError('Could not load 2FA status.');
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  async function handleDisable() {
    if (!confirmingDisable) {
      setConfirmingDisable(true);
      return;
    }
    setDisabling(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiPost('/api/v1/auth/2fa/disable', {}, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not disable 2FA. Please try again.');
        return;
      }
      setEnabled(false);
      setConfirmingDisable(false);
      setSuccess('Two-factor authentication has been disabled.');
      await loadStatus();
    } catch {
      setError('Could not disable 2FA. Please try again.');
    } finally {
      setDisabling(false);
    }
  }

  async function handleStartQr(mode) {
    setQrMode(mode);
    setQrStep('loading');
    setQrError('');
    setQrCode('');
    setQrUrl('');
    setQrSecret('');

    try {
      if (mode === 'enable' && hasSecret) {
        // Existing secret — just fetch QR
        const res = await apiGet('/api/v1/auth/2fa/qr', token);
        if (handleAuthError(res)) return;
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setQrError(data.message || 'Could not load QR code.');
          setQrStep('idle');
          return;
        }
        setQrUrl(data?.data?.qr_url || '');
        setQrSecret(data?.data?.secret || '');
      } else {
        // Generate new secret (reset or no existing secret)
        const res = await apiPost('/api/v1/auth/2fa/setup-reset', {}, token);
        if (handleAuthError(res)) return;
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setQrError(data.message || 'Could not generate QR code.');
          setQrStep('idle');
          return;
        }
        setQrUrl(data?.data?.qr_url || '');
        setQrSecret(data?.data?.secret || '');
      }
      setQrStep('qr');
    } catch {
      setQrError('Could not load QR code.');
      setQrStep('idle');
    }
  }

  async function handleQrConfirm() {
    setQrSubmitting(true);
    setQrError('');
    try {
      const res = await apiPost('/api/v1/auth/2fa/confirm-reset', {
        secret: qrSecret,
        code: qrCode,
      }, token);
      if (handleAuthError(res)) return;
      const data = await res.json().catch(() => ({}));
      if (res.status === 422) {
        setQrError('Invalid or expired code. Try again.');
        return;
      }
      if (!res.ok) {
        setQrError(data.message || 'Could not confirm 2FA.');
        return;
      }
      setQrStep('idle');
      setQrUrl('');
      setQrSecret('');
      setQrCode('');
      setEnabled(true);
      setSuccess(
        qrMode === 'reset'
          ? 'Two-factor authentication has been reset with a new code.'
          : 'Two-factor authentication has been enabled.'
      );
      await loadStatus();
    } catch {
      setQrError('Could not confirm 2FA.');
    } finally {
      setQrSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5" data-testid="rwm-admin-security-loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div data-testid="rwm-admin-security-tab">
      <h2 className="h5 fw-semibold mb-4" style={{ color: 'var(--rwm-dark)' }}>
        Security
      </h2>

      {error ? (
        <Alert variant="danger" className="mb-4" data-testid="rwm-admin-security-error">
          {error}
        </Alert>
      ) : null}

      {success ? (
        <Alert variant="success" className="mb-4" data-testid="rwm-admin-security-success">
          {success}
        </Alert>
      ) : null}

      <Card className="mb-3" style={{ borderColor: 'var(--rwm-border)' }}>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h3 className="h6 fw-semibold mb-1" style={{ color: 'var(--rwm-dark)' }}>
                Two-Factor Authentication
              </h3>
              <p className="text-muted small mb-0">
                Protect your admin account with an authenticator app.
              </p>
            </div>
            <Badge
              bg={enabled ? 'success' : 'secondary'}
              data-testid="rwm-admin-2fa-status-badge"
            >
              {enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>

          {enabled && confirmedAt ? (
            <p className="text-muted small mb-3" data-testid="rwm-admin-2fa-confirmed-at">
              Enabled on {new Date(confirmedAt).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
            </p>
          ) : null}

          <div className="d-flex gap-2 flex-wrap">
            {enabled ? (
              <>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleDisable}
                  disabled={disabling}
                  data-testid="rwm-admin-2fa-disable-btn"
                >
                  {disabling
                    ? 'Disabling…'
                    : confirmingDisable
                    ? 'Click again to confirm disable'
                    : 'Disable 2FA'}
                </Button>
                {confirmingDisable ? (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-muted"
                    onClick={() => setConfirmingDisable(false)}
                    data-testid="rwm-admin-2fa-cancel-disable"
                  >
                    Cancel
                  </Button>
                ) : null}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleStartQr('reset')}
                  disabled={qrStep === 'loading'}
                  data-testid="rwm-admin-2fa-reset-btn"
                >
                  Reset 2FA (lost authenticator)
                </Button>
              </>
            ) : (
              <Button
                variant="info"
                size="sm"
                className="text-white"
                onClick={() => handleStartQr('enable')}
                disabled={qrStep === 'loading'}
                data-testid="rwm-admin-2fa-enable-btn"
              >
                {qrStep === 'loading' ? 'Loading…' : 'Enable 2FA'}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {qrStep === 'qr' ? (
        <Card style={{ borderColor: 'var(--rwm-border)' }}>
          <Card.Body className="p-4">
            <h3 className="h6 fw-semibold mb-2" style={{ color: 'var(--rwm-dark)' }}>
              {qrMode === 'reset' ? 'Reset Two-Factor Authentication' : 'Enable Two-Factor Authentication'}
            </h3>
            <p className="text-muted small mb-3">
              Scan this QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm.
            </p>

            {qrError ? (
              <Alert variant="danger" className="mb-3" data-testid="rwm-admin-2fa-qr-error">
                {qrError}
              </Alert>
            ) : null}

            {qrUrl ? (
              <div className="text-center mb-3">
                <img
                  src={qrUrl}
                  alt="2FA QR code"
                  style={{ maxWidth: '200px' }}
                  data-testid="rwm-admin-2fa-qr-image"
                />
              </div>
            ) : null}

            {qrSecret ? (
              <p className="small text-muted mb-3">
                Manual entry key: <strong data-testid="rwm-admin-2fa-qr-secret">{qrSecret}</strong>
              </p>
            ) : null}

            <Form.Group className="mb-3">
              <Form.Label>Authentication code</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={qrCode}
                onChange={(e) => setQrCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="rwm-admin-2fa-qr-code-input"
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button
                variant="info"
                size="sm"
                className="text-white"
                onClick={handleQrConfirm}
                disabled={qrSubmitting || qrCode.length !== 6}
                data-testid="rwm-admin-2fa-qr-confirm-btn"
              >
                {qrSubmitting ? 'Confirming…' : 'Confirm'}
              </Button>
              <Button
                variant="link"
                size="sm"
                className="text-muted"
                onClick={() => { setQrStep('idle'); setQrError(''); setQrCode(''); }}
                data-testid="rwm-admin-2fa-qr-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}
    </div>
  );
}
